import {useEffect} from "react";
import {useDispatch} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {flex, flexDirection, gap, m, mt, p, w100} from "@pg-design/helpers-css";
import {InputField, TextareaField} from "@pg-design/inputs-module";
import {Link} from "@pg-design/link-module";
import {SystemModal} from "@pg-design/modal-module";
import {FormProviderRHF, useFormRHF} from "@pg-design/react-hook-forms-utils";
import {CheckboxControlled} from "@pg-design/rhf-components-module";
import {getTextVariantStyleFor} from "@pg-design/text";
import {Text} from "@pg-design/text-module";
import {pickBy} from "@pg-mono/nodash";
import {postRequest} from "@pg-mono/request";
import {apiPath} from "@pg-mono/rp-api-routes";
import {rpAppLink} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {validationMessages} from "../../form/utils/validation_messages";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {addSuccessNotification} from "../../notifications/utils/add_success_notification";

interface IProps {
    isOpen: boolean;
    onClose: () => void;
}

interface FormFields {
    name: string;
    email: string;
    url: string;
    content: string;
    consent: boolean;
}
const reportContentSchema = Yup.object().shape({
    name: Yup.string(),
    email: Yup.string().email(validationMessages.email),
    url: Yup.string().required(validationMessages.required),
    content: Yup.string().required(validationMessages.required),
    consent: Yup.boolean().oneOf([true], validationMessages.required)
});

export const ReportContentModal = (props: IProps) => {
    const dispatch = useDispatch();
    const {isMobile} = useUserDevice();
    const theme = useTheme();
    const methods = useFormRHF<FormFields>({
        resolver: yupResolver(reportContentSchema),
        defaultValues: {
            consent: false,
            name: "",
            content: "",
            email: "",
            url: ""
        }
    });

    useEffect(() => {
        if (props.isOpen) {
            setValue("url", window.location.href);
        }
    }, [props.isOpen]);

    const {
        register,
        setValue,
        handleSubmit,
        reset,
        formState: {errors}
    } = methods;

    const closeModal = () => {
        props.onClose();
        reset();
    };

    const onSubmit = (data: FormFields) => {
        closeModal();
        postRequest(
            {},
            apiPath.users.reporting_content,
            pickBy({...data}, (value) => !!value)
        )
            .then(() => {
                dispatch(addSuccessNotification("Twoje zgłoszenie zostało wysłane. Dziękujemy!"));
            })
            .catch(() => {
                dispatch(addFailureNotification("Coś poszło nie tak. Spróbuj później."));
            });
    };

    return (
        <FormProviderRHF {...methods}>
            <SystemModal isOpen={props.isOpen} onModalClose={closeModal} css={modalStyle}>
                <SystemModal.Header headerTextStyle={{textAlign: isMobile ? "left" : "center"}}>Zgłoś nielegalną treść</SystemModal.Header>
                <SystemModal.Content css={p(isMobile ? 1.5 : 3)}>
                    <form css={formWrapper} onSubmit={handleSubmit(onSubmit)}>
                        <InputField {...register("name")} css={inputStyle} error={errors.name?.message} placeholder="Imię i nazwisko (opcjonalnie)" />
                        <InputField {...register("email")} css={inputStyle} error={errors.email?.message} placeholder="E-mail (opcjonalnie)" />
                        <InputField {...register("url")} css={inputStyle} error={errors.url?.message} disabled />
                        <TextareaField
                            {...register("content")}
                            css={inputStyle}
                            placeholder="Uzasadnienie zgłoszenia (napisz nam, dlaczego ta treść jest nielegalna)"
                            error={errors.content?.message}
                        />
                        <div css={inputWrapper}>
                            <CheckboxControlled
                                name="consent"
                                labelContent="Oświadczam, że dokonuję zgłoszenia w dobrej wierze i według mojej najlepszej wiedzy zawarte w nim informacje, w tym zarzuty dotyczące ogłoszenia są prawdziwe."
                                css={labelStyle}
                            />
                            {errors.consent && (
                                <Text color={theme.colors.danger} css={m(0.5, 0, 0)} variant="info_txt_3">
                                    {errors.consent.message}
                                </Text>
                            )}
                        </div>
                        <Text variant="info_txt_1" color="#000">
                            Zgłoszenia możesz dokonać anonimowo. Możesz także podać swoje imię, nazwisko lub adres e-mail. Administratorem Twoich danych
                            osobowych jest <strong>Property Group sp. z o.o.</strong>&nbsp;
                            <Link underline size="small" href={rpAppLink.rodoTerms.base()} target="_blank">
                                Kliknij i dowiedz się więcej o ochronie Twoich danych.
                            </Link>
                        </Text>
                        <Button variant="filled_primary" type="submit" css={submitButton}>
                            PRZEŚLIJ
                        </Button>
                    </form>
                </SystemModal.Content>
            </SystemModal>
        </FormProviderRHF>
    );
};

const modalStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        max-width: 652px;
    }
`;

const formWrapper = css`
    ${flex("flex-start")};
    ${flexDirection("column")};
    ${gap(1.5)};
    ${w100};
`;

const submitButton = css`
    width: 225px;
    align-self: center;
    ${mt(4)};
`;

const inputStyle = css`
    width: 100%;
`;

const inputWrapper = css`
    ${flex("flex-start")};
    ${w100};
    ${flexDirection("column")}
`;

const labelStyle = (theme: Theme) => css`
    ${getTextVariantStyleFor("info_txt_1")(theme)};
    color: ${theme.colors.secondary};
`;
import {useEffect} from "react";
import {useDispatch} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {flex, flexDirection, gap, m, mt, p, w100} from "@pg-design/helpers-css";
import {InputField, TextareaField} from "@pg-design/inputs-module";
import {Link} from "@pg-design/link-module";
import {SystemModal} from "@pg-design/modal-module";
import {FormProviderRHF, useFormRHF} from "@pg-design/react-hook-forms-utils";
import {CheckboxControlled} from "@pg-design/rhf-components-module";
import {getTextVariantStyleFor} from "@pg-design/text";
import {Text} from "@pg-design/text-module";
import {pickBy} from "@pg-mono/nodash";
import {postRequest} from "@pg-mono/request";
import {apiPath} from "@pg-mono/rp-api-routes";
import {rpAppLink} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {validationMessages} from "../../form/utils/validation_messages";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {addSuccessNotification} from "../../notifications/utils/add_success_notification";

interface IProps {
    isOpen: boolean;
    onClose: () => void;
}

interface FormFields {
    name: string;
    email: string;
    url: string;
    content: string;
    consent: boolean;
}
const reportContentSchema = Yup.object().shape({
    name: Yup.string(),
    email: Yup.string().email(validationMessages.email),
    url: Yup.string().required(validationMessages.required),
    content: Yup.string().required(validationMessages.required),
    consent: Yup.boolean().oneOf([true], validationMessages.required)
});

export const ReportContentModal = (props: IProps) => {
    const dispatch = useDispatch();
    const {isMobile} = useUserDevice();
    const theme = useTheme();
    const methods = useFormRHF<FormFields>({
        resolver: yupResolver(reportContentSchema),
        defaultValues: {
            consent: false,
            name: "",
            content: "",
            email: "",
            url: ""
        }
    });

    useEffect(() => {
        if (props.isOpen) {
            setValue("url", window.location.href);
        }
    }, [props.isOpen]);

    const {
        register,
        setValue,
        handleSubmit,
        reset,
        formState: {errors}
    } = methods;

    const closeModal = () => {
        props.onClose();
        reset();
    };

    const onSubmit = (data: FormFields) => {
        closeModal();
        postRequest(
            {},
            apiPath.users.reporting_content,
            pickBy({...data}, (value) => !!value)
        )
            .then(() => {
                dispatch(addSuccessNotification("Twoje zgłoszenie zostało wysłane. Dziękujemy!"));
            })
            .catch(() => {
                dispatch(addFailureNotification("Coś poszło nie tak. Spróbuj później."));
            });
    };

    return (
        <FormProviderRHF {...methods}>
            <SystemModal isOpen={props.isOpen} onModalClose={closeModal} css={modalStyle}>
                <SystemModal.Header headerTextStyle={{textAlign: isMobile ? "left" : "center"}}>Zgłoś nielegalną treść</SystemModal.Header>
                <SystemModal.Content css={p(isMobile ? 1.5 : 3)}>
                    <form css={formWrapper} onSubmit={handleSubmit(onSubmit)}>
                        <InputField {...register("name")} css={inputStyle} error={errors.name?.message} placeholder="Imię i nazwisko (opcjonalnie)" />
                        <InputField {...register("email")} css={inputStyle} error={errors.email?.message} placeholder="E-mail (opcjonalnie)" />
                        <InputField {...register("url")} css={inputStyle} error={errors.url?.message} disabled />
                        <TextareaField
                            {...register("content")}
                            css={inputStyle}
                            placeholder="Uzasadnienie zgłoszenia (napisz nam, dlaczego ta treść jest nielegalna)"
                            error={errors.content?.message}
                        />
                        <div css={inputWrapper}>
                            <CheckboxControlled
                                name="consent"
                                labelContent="Oświadczam, że dokonuję zgłoszenia w dobrej wierze i według mojej najlepszej wiedzy zawarte w nim informacje, w tym zarzuty dotyczące ogłoszenia są prawdziwe."
                                css={labelStyle}
                            />
                            {errors.consent && (
                                <Text color={theme.colors.danger} css={m(0.5, 0, 0)} variant="info_txt_3">
                                    {errors.consent.message}
                                </Text>
                            )}
                        </div>
                        <Text variant="info_txt_1" color="#000">
                            Zgłoszenia możesz dokonać anonimowo. Możesz także podać swoje imię, nazwisko lub adres e-mail. Administratorem Twoich danych
                            osobowych jest <strong>Property Group sp. z o.o.</strong>&nbsp;
                            <Link underline size="small" href={rpAppLink.rodoTerms.base()} target="_blank">
                                Kliknij i dowiedz się więcej o ochronie Twoich danych.
                            </Link>
                        </Text>
                        <Button variant="filled_primary" type="submit" css={submitButton}>
                            PRZEŚLIJ
                        </Button>
                    </form>
                </SystemModal.Content>
            </SystemModal>
        </FormProviderRHF>
    );
};

const modalStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        max-width: 652px;
    }
`;

const formWrapper = css`
    ${flex("flex-start")};
    ${flexDirection("column")};
    ${gap(1.5)};
    ${w100};
`;

const submitButton = css`
    width: 225px;
    align-self: center;
    ${mt(4)};
`;

const inputStyle = css`
    width: 100%;
`;

const inputWrapper = css`
    ${flex("flex-start")};
    ${w100};
    ${flexDirection("column")}
`;

const labelStyle = (theme: Theme) => css`
    ${getTextVariantStyleFor("info_txt_1")(theme)};
    color: ${theme.colors.secondary};
`;
